import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, CheckCircle, Home as HomeIcon, Car, Shield, Umbrella, Heart, Building, Briefcase, FileText, Zap } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CANOPY_CLIENT_ID = 'ba3f8dde-360f-4bec-964c-11abfdfc45eb';

const AdvancedQuote = () => {
  const [submissionId, setSubmissionId] = useState('');
  const [basicInfo, setBasicInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [dataSource, setDataSource] = useState(''); // 'manual' or 'canopy'
  const [canopyData, setCanopyData] = useState(null);
  
  // Coverage-specific data
  const [homeData, setHomeData] = useState({
    propertyType: '',
    yearBuilt: '',
    squareFootage: '',
    numberOfBedrooms: '',
    numberOfBathrooms: '',
    roofAge: '',
    roofType: '',
    heatingType: '',
    foundationType: '',
    estimatedValue: '',
    mortgageBalance: '',
    desiredCoverageAmount: '',
    deductiblePreference: '',
    additionalStructures: '',
    securityFeatures: '',
    claimHistory: ''
  });

  const [autoData, setAutoData] = useState({
    vehicles: [{
      year: '',
      make: '',
      model: '',
      vin: '',
      usage: '',
      annualMileage: '',
      primaryDriver: '',
      ownershipStatus: ''
    }]
  });

  const [lifeData, setLifeData] = useState({
    coverageAmount: '',
    termLength: '',
    policyType: '',
    beneficiaries: '',
    healthStatus: '',
    tobaccoUse: '',
    occupation: '',
    hobbies: ''
  });

  const [umbrellaData, setUmbrellaData] = useState({
    desiredCoverage: '',
    existingLiability: '',
    numberOfVehicles: '',
    numberOfProperties: '',
    rentedProperties: '',
    recreationalVehicles: ''
  });

  const [businessData, setBusinessData] = useState({
    businessName: '',
    businessType: '',
    yearsInBusiness: '',
    numberOfEmployees: '',
    annualRevenue: '',
    businessLocation: '',
    coverageNeeds: '',
    existingPolicies: ''
  });

  useEffect(() => {
    const loadSubmissionData = () => {
      const subId = localStorage.getItem('submissionId');
      
      if (!subId) {
        toast.error('No submission found. Redirecting to home...');
        setTimeout(() => window.location.href = '/', 2000);
        return;
      }

      setSubmissionId(subId);
      
      // Try to get basic info from localStorage
      const storedData = localStorage.getItem(`formData_${subId}`);
      if (storedData) {
        try {
          const data = JSON.parse(storedData);
          setBasicInfo(data);
        } catch (error) {
          console.error('Error parsing stored data:', error);
        }
      }
      
      setLoading(false);
    };

    loadSubmissionData();

    // Listen for Canopy Connect events
    window.addEventListener('message', handleCanopyMessage);
    
    return () => {
      window.removeEventListener('message', handleCanopyMessage);
    };
  }, []);

  const handleCanopyMessage = (event) => {
    // Verify message is from Canopy Connect
    if (event.origin !== 'https://connect.canopyservicing.com') return;
    
    try {
      const data = JSON.parse(event.data);
      
      if (data.type === 'CANOPY_CONNECT_SUCCESS') {
        setCanopyData(data.payload);
        toast.success('Insurance information imported successfully!');
        // Auto-submit to Zapier with Canopy data
        submitCanopyData(data.payload);
      } else if (data.type === 'CANOPY_CONNECT_ERROR') {
        toast.error('Unable to import data from Canopy Connect');
        console.error('Canopy error:', data.error);
      } else if (data.type === 'CANOPY_CONNECT_CLOSED') {
        // User closed Canopy without completing
        setDataSource('');
      }
    } catch (error) {
      console.error('Error parsing Canopy message:', error);
    }
  };

  const openCanopyConnect = () => {
    setDataSource('canopy');
    
    // Canopy Connect URL with client ID
    const canopyUrl = `https://connect.canopyservicing.com/connect?client_id=${CANOPY_CLIENT_ID}&user_id=${submissionId}`;
    
    // Open Canopy Connect in a popup window
    const width = 500;
    const height = 700;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;
    
    window.open(
      canopyUrl,
      'CanopyConnect',
      `width=${width},height=${height},left=${left},top=${top},toolbar=0,menubar=0`
    );
  };

  const submitCanopyData = async (canopyPayload) => {
    setSubmitting(true);

    try {
      const detailedQuote = {
        submission_id: submissionId,
        data_source: 'canopy_connect',
        basic_info: basicInfo,
        canopy_data: canopyPayload,
        submitted_at: new Date().toISOString()
      };

      // Send to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/submit-advanced-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...detailedQuote,
          form_type: 'advanced_quote_canopy'
        })
      });

      const result = await response.json();
      if (result.status !== 'success') {
        throw new Error(result.message);
      }

      setSubmitted(true);
      toast.success('Information submitted successfully!');
    } catch (error) {
      console.error('Error submitting Canopy data:', error);
      toast.error('Unable to submit. Please try again or contact us at (970) 315-4628');
    } finally {
      setSubmitting(false);
    }
  };

  const coverageIcons = {
    'Home': HomeIcon,
    'Auto': Car,
    'Specialty': Shield,
    'Umbrella': Umbrella,
    'Life': Heart,
    'Annuities': Building,
    'Business/Commercial': Briefcase,
    'Other': FileText
  };

  const addVehicle = () => {
    setAutoData({
      ...autoData,
      vehicles: [...autoData.vehicles, {
        year: '',
        make: '',
        model: '',
        vin: '',
        usage: '',
        annualMileage: '',
        primaryDriver: '',
        ownershipStatus: ''
      }]
    });
  };

  const removeVehicle = (index) => {
    const newVehicles = autoData.vehicles.filter((_, i) => i !== index);
    setAutoData({ ...autoData, vehicles: newVehicles });
  };

  const updateVehicle = (index, field, value) => {
    const newVehicles = [...autoData.vehicles];
    newVehicles[index][field] = value;
    setAutoData({ ...autoData, vehicles: newVehicles });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const detailedQuote = {
        submission_id: submissionId,
        basic_info: basicInfo,
        detailed_coverage: {
          home: basicInfo?.coverages?.includes('Home') ? homeData : null,
          auto: basicInfo?.coverages?.includes('Auto') ? autoData : null,
          life: basicInfo?.coverages?.includes('Life') ? lifeData : null,
          umbrella: basicInfo?.coverages?.includes('Umbrella') ? umbrellaData : null,
          business: basicInfo?.coverages?.includes('Business/Commercial') ? businessData : null
        },
        submitted_at: new Date().toISOString()
      };

      // Send to backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/submit-advanced-quote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...detailedQuote,
          form_type: 'advanced_quote'
        })
      });

      const result = await response.json();
      if (result.status !== 'success') {
        throw new Error(result.message);
      }

      setSubmitted(true);
      toast.success('Detailed quote submitted successfully!');
    } catch (error) {
      console.error('Error submitting detailed quote:', error);
      toast.error('Unable to submit. Please try again or contact us at (970) 315-4628');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#003087] mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your information...</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#003087] via-[#003087] to-[#b4c4ea] flex items-center justify-center px-4">
        <div className="max-w-2xl w-full bg-white rounded-lg shadow-2xl p-8 md:p-12 text-center">
          <CheckCircle className="h-20 w-20 text-green-500 mx-auto mb-6" />
          <h1 className="text-4xl font-bold text-[#003087] mb-4">Thank You!</h1>
          <p className="text-lg text-gray-700 mb-8">
            Your detailed quote information has been submitted. An agent will review your information and contact you shortly with personalized recommendations.
          </p>
          <Button
            onClick={() => window.location.href = '/'}
            className="bg-[#003087] hover:bg-[#003087]/90 text-white"
          >
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const selectedCoverages = basicInfo?.coverages || [];

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-5xl mx-auto">
        <button
          onClick={() => window.location.href = '/thank-you'}
          className="flex items-center text-[#003087] hover:underline mb-6"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          Back to Confirmation
        </button>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-[#003087] mb-2">Advanced Quote Request</h1>
          <p className="text-gray-600 mb-6">
            Provide detailed information to help us create the most accurate quote for your needs
          </p>

          {/* Choose Data Entry Method */}
          {!dataSource && (
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-[#003087] mb-4">Choose How to Provide Your Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Canopy Connect Option */}
                <button
                  onClick={openCanopyConnect}
                  className="group relative p-6 border-2 border-[#003087] rounded-lg hover:bg-[#003087] transition-all duration-300 text-left"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-[#003087] group-hover:bg-white rounded-full flex items-center justify-center transition-colors">
                        <Zap className="h-6 w-6 text-white group-hover:text-[#003087]" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-[#003087] group-hover:text-white mb-2 transition-colors">
                        Use Canopy Connect
                      </h4>
                      <p className="text-sm text-gray-600 group-hover:text-white/90 mb-3 transition-colors">
                        Securely import your existing insurance information in seconds
                      </p>
                      <ul className="text-sm text-gray-700 group-hover:text-white/80 space-y-1 transition-colors">
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          Fast & secure
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          Auto-fills your information
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          No manual typing
                        </li>
                      </ul>
                      <div className="mt-4 text-[#003087] group-hover:text-white font-semibold">
                        Recommended →
                      </div>
                    </div>
                  </div>
                </button>

                {/* Manual Entry Option */}
                <button
                  onClick={() => setDataSource('manual')}
                  className="group relative p-6 border-2 border-gray-300 rounded-lg hover:border-[#003087] hover:bg-gray-50 transition-all duration-300 text-left"
                >
                  <div className="flex items-start space-x-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-gray-300 group-hover:bg-[#003087] rounded-full flex items-center justify-center transition-colors">
                        <FileText className="h-6 w-6 text-gray-700 group-hover:text-white" />
                      </div>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-lg font-bold text-gray-800 group-hover:text-[#003087] mb-2 transition-colors">
                        Fill Out Manually
                      </h4>
                      <p className="text-sm text-gray-600 mb-3">
                        Enter your information using our detailed form
                      </p>
                      <ul className="text-sm text-gray-600 space-y-1">
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          Complete control
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          Add custom details
                        </li>
                        <li className="flex items-center">
                          <CheckCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                          No login required
                        </li>
                      </ul>
                    </div>
                  </div>
                </button>
              </div>

              <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>What is Canopy Connect?</strong> Canopy Connect securely connects to your existing insurance providers to automatically retrieve your policy information, saving you time and ensuring accuracy.
                </p>
              </div>
            </div>
          )}

          {/* Manual Form (only show if manual is selected) */}
          {dataSource === 'manual' && (
            <>
              <div className="mb-6 flex items-center justify-between bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-600">Filling out manually</p>
                  <p className="text-xs text-gray-500">This may take 10-15 minutes to complete</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDataSource('')}
                  className="border-[#003087] text-[#003087]"
                >
                  ← Change Method
                </Button>
              </div>

          {/* Basic Info Summary */}
          {basicInfo && (
            <div className="bg-[#b4c4ea]/20 rounded-lg p-6 mb-8">
              <h3 className="font-semibold text-[#003087] mb-3">Your Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Name:</span>
                  <span className="ml-2 font-medium">{basicInfo.firstName} {basicInfo.lastName}</span>
                </div>
                <div>
                  <span className="text-gray-600">Email:</span>
                  <span className="ml-2 font-medium">{basicInfo.email}</span>
                </div>
                <div>
                  <span className="text-gray-600">Phone:</span>
                  <span className="ml-2 font-medium">{basicInfo.phoneNumber}</span>
                </div>
                <div>
                  <span className="text-gray-600">Address:</span>
                  <span className="ml-2 font-medium">{basicInfo.city}, {basicInfo.state} {basicInfo.zipCode}</span>
                </div>
              </div>
              <div className="mt-4">
                <span className="text-gray-600">Coverage Types:</span>
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedCoverages.map((coverage) => {
                    const Icon = coverageIcons[coverage] || FileText;
                    return (
                      <span key={coverage} className="inline-flex items-center bg-[#003087] text-white px-3 py-1 rounded-full text-sm">
                        <Icon className="h-4 w-4 mr-2" />
                        {coverage}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-8">
            
            {/* Home Insurance Section */}
            {selectedCoverages.includes('Home') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-[#003087]">
                    <HomeIcon className="h-6 w-6 mr-2" />
                    Home Insurance Details
                  </CardTitle>
                  <CardDescription>Tell us about the property you want to insure</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="propertyType">Property Type *</Label>
                      <Select value={homeData.propertyType} onValueChange={(value) => setHomeData({...homeData, propertyType: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="single-family">Single Family Home</SelectItem>
                          <SelectItem value="condo">Condo</SelectItem>
                          <SelectItem value="townhouse">Townhouse</SelectItem>
                          <SelectItem value="multi-family">Multi-Family</SelectItem>
                          <SelectItem value="mobile">Mobile Home</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div>
                      <Label htmlFor="yearBuilt">Year Built *</Label>
                      <Input
                        id="yearBuilt"
                        type="number"
                        value={homeData.yearBuilt}
                        onChange={(e) => setHomeData({...homeData, yearBuilt: e.target.value})}
                        placeholder="e.g., 2010"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="squareFootage">Square Footage *</Label>
                      <Input
                        id="squareFootage"
                        type="number"
                        value={homeData.squareFootage}
                        onChange={(e) => setHomeData({...homeData, squareFootage: e.target.value})}
                        placeholder="e.g., 2500"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="estimatedValue">Estimated Home Value *</Label>
                      <Input
                        id="estimatedValue"
                        type="text"
                        value={homeData.estimatedValue}
                        onChange={(e) => setHomeData({...homeData, estimatedValue: e.target.value})}
                        placeholder="e.g., $450,000"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="numberOfBedrooms">Bedrooms</Label>
                      <Input
                        id="numberOfBedrooms"
                        type="number"
                        value={homeData.numberOfBedrooms}
                        onChange={(e) => setHomeData({...homeData, numberOfBedrooms: e.target.value})}
                        placeholder="e.g., 3"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="numberOfBathrooms">Bathrooms</Label>
                      <Input
                        id="numberOfBathrooms"
                        type="number"
                        step="0.5"
                        value={homeData.numberOfBathrooms}
                        onChange={(e) => setHomeData({...homeData, numberOfBathrooms: e.target.value})}
                        placeholder="e.g., 2.5"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="roofAge">Roof Age (years)</Label>
                      <Input
                        id="roofAge"
                        type="number"
                        value={homeData.roofAge}
                        onChange={(e) => setHomeData({...homeData, roofAge: e.target.value})}
                        placeholder="e.g., 5"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="roofType">Roof Type</Label>
                      <Select value={homeData.roofType} onValueChange={(value) => setHomeData({...homeData, roofType: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select roof type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="asphalt">Asphalt Shingles</SelectItem>
                          <SelectItem value="metal">Metal</SelectItem>
                          <SelectItem value="tile">Tile</SelectItem>
                          <SelectItem value="slate">Slate</SelectItem>
                          <SelectItem value="wood">Wood Shake</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="securityFeatures">Security Features</Label>
                    <Textarea
                      id="securityFeatures"
                      value={homeData.securityFeatures}
                      onChange={(e) => setHomeData({...homeData, securityFeatures: e.target.value})}
                      placeholder="e.g., Alarm system, deadbolts, security cameras"
                      className="mt-2"
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="claimHistory">Previous Claims History</Label>
                    <Textarea
                      id="claimHistory"
                      value={homeData.claimHistory}
                      onChange={(e) => setHomeData({...homeData, claimHistory: e.target.value})}
                      placeholder="List any insurance claims in the past 5 years"
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Auto Insurance Section */}
            {selectedCoverages.includes('Auto') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-[#003087]">
                    <Car className="h-6 w-6 mr-2" />
                    Auto Insurance Details
                  </CardTitle>
                  <CardDescription>Provide information about each vehicle you want to insure</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {autoData.vehicles.map((vehicle, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-4">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="font-semibold text-[#003087]">Vehicle {index + 1}</h4>
                        {autoData.vehicles.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => removeVehicle(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            Remove
                          </Button>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Year *</Label>
                          <Input
                            type="number"
                            value={vehicle.year}
                            onChange={(e) => updateVehicle(index, 'year', e.target.value)}
                            placeholder="e.g., 2020"
                            className="mt-2"
                            required
                          />
                        </div>

                        <div>
                          <Label>Make *</Label>
                          <Input
                            value={vehicle.make}
                            onChange={(e) => updateVehicle(index, 'make', e.target.value)}
                            placeholder="e.g., Toyota"
                            className="mt-2"
                            required
                          />
                        </div>

                        <div>
                          <Label>Model *</Label>
                          <Input
                            value={vehicle.model}
                            onChange={(e) => updateVehicle(index, 'model', e.target.value)}
                            placeholder="e.g., Camry"
                            className="mt-2"
                            required
                          />
                        </div>

                        <div>
                          <Label>VIN</Label>
                          <Input
                            value={vehicle.vin}
                            onChange={(e) => updateVehicle(index, 'vin', e.target.value)}
                            placeholder="17-character VIN"
                            className="mt-2"
                            maxLength={17}
                          />
                        </div>

                        <div>
                          <Label>Primary Use</Label>
                          <Select value={vehicle.usage} onValueChange={(value) => updateVehicle(index, 'usage', value)}>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Select usage" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="commute">Commute to Work</SelectItem>
                              <SelectItem value="pleasure">Pleasure/Personal</SelectItem>
                              <SelectItem value="business">Business</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div>
                          <Label>Annual Mileage</Label>
                          <Input
                            type="number"
                            value={vehicle.annualMileage}
                            onChange={(e) => updateVehicle(index, 'annualMileage', e.target.value)}
                            placeholder="e.g., 12000"
                            className="mt-2"
                          />
                        </div>

                        <div>
                          <Label>Ownership Status</Label>
                          <Select value={vehicle.ownershipStatus} onValueChange={(value) => updateVehicle(index, 'ownershipStatus', value)}>
                            <SelectTrigger className="mt-2">
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="owned">Owned</SelectItem>
                              <SelectItem value="financed">Financed</SelectItem>
                              <SelectItem value="leased">Leased</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={addVehicle}
                    className="w-full border-[#003087] text-[#003087] hover:bg-[#003087] hover:text-white"
                  >
                    + Add Another Vehicle
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Life Insurance Section */}
            {selectedCoverages.includes('Life') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-[#003087]">
                    <Heart className="h-6 w-6 mr-2" />
                    Life Insurance Details
                  </CardTitle>
                  <CardDescription>Help us understand your life insurance needs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="coverageAmount">Desired Coverage Amount *</Label>
                      <Input
                        id="coverageAmount"
                        value={lifeData.coverageAmount}
                        onChange={(e) => setLifeData({...lifeData, coverageAmount: e.target.value})}
                        placeholder="e.g., $500,000"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="policyType">Policy Type</Label>
                      <Select value={lifeData.policyType} onValueChange={(value) => setLifeData({...lifeData, policyType: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="term">Term Life</SelectItem>
                          <SelectItem value="whole">Whole Life</SelectItem>
                          <SelectItem value="universal">Universal Life</SelectItem>
                          <SelectItem value="not-sure">Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="termLength">Term Length (if applicable)</Label>
                      <Select value={lifeData.termLength} onValueChange={(value) => setLifeData({...lifeData, termLength: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select term" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10 Years</SelectItem>
                          <SelectItem value="15">15 Years</SelectItem>
                          <SelectItem value="20">20 Years</SelectItem>
                          <SelectItem value="30">30 Years</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="healthStatus">General Health Status</Label>
                      <Select value={lifeData.healthStatus} onValueChange={(value) => setLifeData({...lifeData, healthStatus: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="excellent">Excellent</SelectItem>
                          <SelectItem value="good">Good</SelectItem>
                          <SelectItem value="fair">Fair</SelectItem>
                          <SelectItem value="poor">Poor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="tobaccoUse">Tobacco Use</Label>
                      <Select value={lifeData.tobaccoUse} onValueChange={(value) => setLifeData({...lifeData, tobaccoUse: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select option" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="no">No</SelectItem>
                          <SelectItem value="yes">Yes</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="occupation">Occupation</Label>
                      <Input
                        id="occupation"
                        value={lifeData.occupation}
                        onChange={(e) => setLifeData({...lifeData, occupation: e.target.value})}
                        placeholder="Your occupation"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="beneficiaries">Beneficiaries</Label>
                    <Textarea
                      id="beneficiaries"
                      value={lifeData.beneficiaries}
                      onChange={(e) => setLifeData({...lifeData, beneficiaries: e.target.value})}
                      placeholder="List primary beneficiaries (e.g., Spouse, Children)"
                      className="mt-2"
                      rows={3}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Umbrella Insurance Section */}
            {selectedCoverages.includes('Umbrella') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-[#003087]">
                    <Umbrella className="h-6 w-6 mr-2" />
                    Umbrella Insurance Details
                  </CardTitle>
                  <CardDescription>Additional liability protection beyond your primary policies</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="desiredCoverage">Desired Coverage Amount</Label>
                      <Select value={umbrellaData.desiredCoverage} onValueChange={(value) => setUmbrellaData({...umbrellaData, desiredCoverage: value})}>
                        <SelectTrigger className="mt-2">
                          <SelectValue placeholder="Select amount" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1m">$1 Million</SelectItem>
                          <SelectItem value="2m">$2 Million</SelectItem>
                          <SelectItem value="3m">$3 Million</SelectItem>
                          <SelectItem value="5m">$5 Million</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="numberOfVehicles">Number of Vehicles</Label>
                      <Input
                        id="numberOfVehicles"
                        type="number"
                        value={umbrellaData.numberOfVehicles}
                        onChange={(e) => setUmbrellaData({...umbrellaData, numberOfVehicles: e.target.value})}
                        placeholder="e.g., 2"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="numberOfProperties">Number of Properties</Label>
                      <Input
                        id="numberOfProperties"
                        type="number"
                        value={umbrellaData.numberOfProperties}
                        onChange={(e) => setUmbrellaData({...umbrellaData, numberOfProperties: e.target.value})}
                        placeholder="e.g., 1"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="recreationalVehicles">Recreational Vehicles</Label>
                      <Input
                        id="recreationalVehicles"
                        value={umbrellaData.recreationalVehicles}
                        onChange={(e) => setUmbrellaData({...umbrellaData, recreationalVehicles: e.target.value})}
                        placeholder="e.g., Boat, ATV, Motorcycle"
                        className="mt-2"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Business Insurance Section */}
            {selectedCoverages.includes('Business/Commercial') && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center text-[#003087]">
                    <Briefcase className="h-6 w-6 mr-2" />
                    Business Insurance Details
                  </CardTitle>
                  <CardDescription>Information about your business and coverage needs</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="businessName">Business Name *</Label>
                      <Input
                        id="businessName"
                        value={businessData.businessName}
                        onChange={(e) => setBusinessData({...businessData, businessName: e.target.value})}
                        placeholder="Your business name"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="businessType">Business Type *</Label>
                      <Input
                        id="businessType"
                        value={businessData.businessType}
                        onChange={(e) => setBusinessData({...businessData, businessType: e.target.value})}
                        placeholder="e.g., Retail, Consulting"
                        className="mt-2"
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="yearsInBusiness">Years in Business</Label>
                      <Input
                        id="yearsInBusiness"
                        type="number"
                        value={businessData.yearsInBusiness}
                        onChange={(e) => setBusinessData({...businessData, yearsInBusiness: e.target.value})}
                        placeholder="e.g., 5"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="numberOfEmployees">Number of Employees</Label>
                      <Input
                        id="numberOfEmployees"
                        type="number"
                        value={businessData.numberOfEmployees}
                        onChange={(e) => setBusinessData({...businessData, numberOfEmployees: e.target.value})}
                        placeholder="e.g., 10"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="annualRevenue">Annual Revenue</Label>
                      <Input
                        id="annualRevenue"
                        value={businessData.annualRevenue}
                        onChange={(e) => setBusinessData({...businessData, annualRevenue: e.target.value})}
                        placeholder="e.g., $500,000"
                        className="mt-2"
                      />
                    </div>

                    <div>
                      <Label htmlFor="businessLocation">Business Location</Label>
                      <Input
                        id="businessLocation"
                        value={businessData.businessLocation}
                        onChange={(e) => setBusinessData({...businessData, businessLocation: e.target.value})}
                        placeholder="City, State"
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="coverageNeeds">Coverage Needs</Label>
                    <Textarea
                      id="coverageNeeds"
                      value={businessData.coverageNeeds}
                      onChange={(e) => setBusinessData({...businessData, coverageNeeds: e.target.value})}
                      placeholder="What types of business coverage are you interested in? (e.g., General Liability, Professional Liability, Workers Comp)"
                      className="mt-2"
                      rows={4}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Submit Button */}
            <div className="flex justify-end space-x-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => window.location.href = '/thank-you'}
                className="border-[#003087] text-[#003087]"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitting}
                className="bg-[#e01933] hover:bg-[#b30032] text-white px-8"
              >
                {submitting ? 'Submitting...' : 'Submit Detailed Quote'}
              </Button>
            </div>
          </form>
            </>
          )}

          {/* Canopy Loading State */}
          {dataSource === 'canopy' && !canopyData && (
            <div className="text-center py-12">
              <div className="mb-6">
                <Zap className="h-16 w-16 text-[#003087] mx-auto animate-pulse" />
              </div>
              <h3 className="text-xl font-bold text-[#003087] mb-2">Connecting to Canopy</h3>
              <p className="text-gray-600 mb-4">Please complete the process in the popup window</p>
              <Button
                variant="outline"
                onClick={() => setDataSource('')}
                className="border-[#003087] text-[#003087]"
              >
                Cancel & Choose Another Method
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdvancedQuote;
